import React from "react";
import { motion } from "framer-motion";
import {
  Server,
  Users,
  LineChart,
  Workflow,
  Gauge,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: Server,
    title: "Enterprise IT",
    blurb:
      "Architecture, platform engineering and integrations for organisations that cannot afford to slow down.",
    span: "lg:col-span-2 lg:row-span-2",
    hero: true,
    testid: "service-enterprise-it",
  },
  {
    icon: Users,
    title: "Stakeholder Management",
    blurb:
      "Coordinated operating systems for people, partners, vendors and customers.",
    span: "lg:col-span-2",
    testid: "service-stakeholder",
  },
  {
    icon: Workflow,
    title: "Process Automation",
    blurb:
      "Mapping, redesigning and automating the workflows that quietly leak time and revenue.",
    span: "lg:col-span-2",
    testid: "service-automation",
  },
  {
    icon: LineChart,
    title: "Resource Planning",
    blurb: "Stock, finance, people, place — under one operating canvas.",
    span: "lg:col-span-2",
    testid: "service-rpa",
  },
  {
    icon: Gauge,
    title: "KPI / MIS Visibility",
    blurb:
      "Real-time dashboards engineered for the operator, not the analyst.",
    span: "lg:col-span-2",
    testid: "service-kpi",
  },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 lg:py-32 bg-[#f5f5f7] border-y border-[#e5e5e5]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs text-[#0038FF]">[ 02 ]</span>
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0038FF]">
                What We Do
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid="services-heading"
              className="font-display text-[2.4rem] sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#0a0a0a] break-words"
            >
              Services, sharpened
              <br />
              around outcomes.
            </motion.h2>
          </div>
          <p className="text-base text-[#4a4a4a] max-w-md leading-relaxed">
            From the first whiteboard sketch to a system running at scale, we
            stay across the lifecycle — discovery, build, rollout, optimisation.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 auto-rows-fr gap-4 md:gap-6">
          {services.map((svc, idx) => {
            const Icon = svc.icon;
            return (
              <motion.div
                key={svc.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.7,
                  delay: idx * 0.06,
                  ease: [0.16, 1, 0.3, 1],
                }}
                data-testid={svc.testid}
                className={`group relative bg-white border border-[#e5e5e5] p-8 md:p-10 flex flex-col justify-between card-sharp-shadow ${
                  svc.span
                } ${svc.hero ? "min-h-[320px]" : "min-h-[220px]"}`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`flex items-center justify-center ${
                      svc.hero ? "w-14 h-14" : "w-11 h-11"
                    } border border-[#0a0a0a] text-[#0a0a0a] group-hover:bg-[#0038FF] group-hover:border-[#0038FF] group-hover:text-white transition-colors`}
                  >
                    <Icon
                      className={`${svc.hero ? "w-7 h-7" : "w-5 h-5"}`}
                      strokeWidth={1.75}
                    />
                  </div>
                  <ArrowUpRight
                    className="w-5 h-5 text-[#4a4a4a] group-hover:text-[#0038FF] transition-colors"
                    strokeWidth={1.75}
                  />
                </div>
                <div>
                  <h3
                    className={`font-display font-bold text-[#0a0a0a] tracking-tight ${
                      svc.hero
                        ? "text-3xl lg:text-4xl mb-4"
                        : "text-xl lg:text-2xl mb-3"
                    }`}
                  >
                    {svc.title}
                  </h3>
                  <p
                    className={`text-[#4a4a4a] leading-relaxed ${
                      svc.hero ? "text-base max-w-md" : "text-sm"
                    }`}
                  >
                    {svc.blurb}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
